"use client";

import { CreditCard, ExternalLink, WalletCards } from "lucide-react";

import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import type { HostedPaymentLabels } from "./qris-payment-step.helpers";

interface HostedPaymentPanelProps {
  labels: HostedPaymentLabels;
  invoiceUrl: string;
  paymentId: string;
  amount: number;
  guest: boolean;
}

export function HostedPaymentPanel({
  labels,
  invoiceUrl,
  paymentId,
  amount,
  guest,
}: HostedPaymentPanelProps) {
  const handleContinue = () => {
    trackEvent("hosted_checkout_opened", {
      paymentId,
      amount,
      guest,
      paymentMethods: "credit_card,e_wallet",
      provider: "singapay",
    });
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="space-y-1">
        <h3 className="text-lg font-semibold text-text-heading">
          {labels.title}
        </h3>
        <p className="text-sm text-text-muted">{labels.instruction}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-border-primary bg-surface-secondary p-4">
          <CreditCard
            className="mb-3 h-5 w-5 text-brand-primary"
            aria-hidden="true"
          />
          <p className="font-medium text-text-heading">{labels.cardTitle}</p>
          <p className="mt-1 text-xs text-text-muted">{labels.cardMethods}</p>
        </div>

        <div className="rounded-xl border border-border-primary bg-surface-secondary p-4">
          <WalletCards
            className="mb-3 h-5 w-5 text-brand-primary"
            aria-hidden="true"
          />
          <p className="font-medium text-text-heading">{labels.walletTitle}</p>
          <p className="mt-1 text-xs text-text-muted">{labels.walletMethods}</p>
        </div>
      </div>

      <Button
        asChild
        className="min-h-11 w-full gap-2 bg-brand-primary text-white hover:bg-brand-primary-hover"
      >
        <a href={invoiceUrl} rel="noreferrer" onClick={handleContinue}>
          {labels.continue}
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
        </a>
      </Button>
      <p className="text-center text-xs text-text-muted">{labels.feeNote}</p>
    </div>
  );
}
